"use client";

import React from "react";
import { BookOpen, BrainCircuit, Rocket, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const tiers = [{
  name: "Workshops",
  description: "Host or join creative sessions, from traditional Balinese crafts to modern arts in a light-filled, inspiring setting.",
  icon: BookOpen
}, {
  name: "Kids Parties",
  description: "The perfect venue for memorable birthdays, featuring safe play areas and customizable themes for little explorers.",
  icon: BrainCircuit
}, {
  name: "Community",
  description: "A gathering spot for local meetups, skill-sharing circles, and collaborative projects in a warm, artistic atmosphere.",
  icon: Rocket
}];
export default function CoursesOverview() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: cardsRef,
    isVisible: areCardsVisible
  } = useRevealOnScroll({
    threshold: 0.1,
    once: true
  });
  return <section data-section-id="136119" id="kilig-klub-creative-corner" className="bg-background text-foreground relative min-h-[90vh] flex flex-col justify-center overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1505473/img/ubud_workshop_space_hero.png)] bg-cover bg-center opacity-30 mix-blend-multiply" />
        {}
        <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/90 to-background" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,var(--background)_100%)] opacity-60" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div ref={headerRef} className={`max-w-4xl mx-auto text-center transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/10 border border-secondary/20 text-secondary text-sm font-medium mb-6">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-secondary opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-secondary"></span>
            </span>
            Kilig Klub @ Ubud Event Space
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-primary mb-6 font-sans">
            Creative Workshop & <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Kids Venue</span> in Ubud
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
            A vibrant sanctuary designed for inspiration, learning, and play. From hands-on artistic workshops to magical kids' birthday parties, we provide the flexible indoor space and warm hospitality you need to create lasting memories.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" className="w-full sm:w-auto group bg-primary text-primary-foreground hover:bg-primary/90" asChild>
              <Link to="/WorkshopSpacePage">
                View Workshop Space
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="w-full sm:w-auto border-primary text-primary hover:bg-primary/5" asChild>
              <Link to="/ConnectPage">
                Book via WhatsApp
              </Link>
            </Button>
          </div>
        </div>

        {}
        <div ref={cardsRef} className={`grid grid-cols-1 md:grid-cols-3 gap-6 mt-24 max-w-5xl mx-auto transition-all duration-1000 delay-300 ease-out ${areCardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {tiers.map((tier, index) => {
          const Icon = tier.icon;
          return <div key={tier.name} data-index={index} className="group relative bg-card/60 backdrop-blur-md border border-border/50 rounded-2xl p-8 hover:bg-card hover:border-secondary/50 transition-all duration-300 overflow-hidden shadow-sm hover:shadow-lg">
                {}
                <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative z-10">
                  <div className="w-12 h-12 rounded-xl bg-background/80 border border-border/50 flex items-center justify-center mb-6 group-hover:scale-110 group-hover:border-secondary/30 transition-all duration-300">
                    <Icon className="w-6 h-6 text-secondary" />
                  </div>
                  <h3 className="text-xl font-semibold text-primary mb-3">
                    {tier.name}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {tier.description}
                  </p>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}